import type { SimEvent } from "../protocol/events";
import type { ForecastEntry } from "../protocol/snapshot";
import { createRng } from "./rng";
import type { EngineState } from "./state";
import { runTick } from "./tick";

/**
 * Phase 2's Forecast (`00-pillars.md` §3, spec 0018): dry-runs a clone of the
 * board for the coming turn's ticks and reports every hazard threshold it
 * would cross if the player played nothing more. It calls the same `runTick`
 * a real turn does — a second implementation of the rules is exactly what
 * this spec exists to avoid — so it can never drift from the hazards it's
 * reporting on.
 */

/** Which of the six forecastable SimEvents each crossing maps to, and where its `targetId` lives. Every other event is ignored — this is a filter, not a SimEvent variant. */
function forecastEntryFrom(event: SimEvent): { kind: ForecastEntry["kind"]; targetId: string } | null {
  switch (event.type) {
    case "node.overheatWarning":
      return { kind: "overheatWarning", targetId: event.nodeId };
    case "node.exploded":
      return { kind: "explosion", targetId: event.nodeId };
    case "pipe.burst":
      return { kind: "pressureBurst", targetId: event.edgeId };
    case "pipe.leaking":
      return { kind: "leak", targetId: event.edgeId };
    case "packet.sludged":
      // nodeId, not packetId: a dry-run clone mints its own packet ids, which
      // never match the real run's — only node/edge ids are stable across
      // the two.
      return { kind: "residenceSludge", targetId: event.nodeId };
    case "catalyst.poisoned":
      return { kind: "catalystPoisoned", targetId: event.nodeId };
    default:
      return null;
  }
}

/**
 * Clones everything `runTick` can mutate, minus two deliberate exceptions:
 * `stage` is immutable authored content, so cloning it would be a wasted
 * allocation every time the forecast recomputes; `rng` carries closures that
 * `structuredClone` can't touch, and — the spec's central risk — reusing the
 * real generator by reference would let the dry run advance the position
 * every subsequent real draw depends on. `createRng(rng.getState())`
 * reconstructs a generator at exactly the real one's position, so advancing
 * the clone can never advance the real `state.rng`.
 */
function cloneForDryRun(state: EngineState): EngineState {
  const { stage, rng, ...rest } = state;
  return {
    ...structuredClone(rest),
    stage,
    rng: createRng(rng.getState()),
  };
}

/**
 * Empty outside Play (constraint: "the forecast is computed as state, not
 * during projection" — `EngineDriver` calls this on entering Play and after
 * each accepted command, never from `projectSnapshot`). Otherwise dry-runs a
 * throwaway clone for up to `ticksPerTurn` ticks, stopping early if the run
 * would end, and collects the first tick each of the six hazard crossings
 * fires on — a threshold that stays crossed for the rest of the turn is
 * reported once, at the crossing, not on every tick it remains true.
 */
// Takes no card catalogue on purpose: a forecast is what the board does on its
// own, and `runTick` needs nothing else. A future forecast that must reason
// about a card can take one then.
export function computeForecast(state: EngineState): ForecastEntry[] {
  if (state.phase !== "play") return [];

  const clone = cloneForDryRun(state);
  clone.phase = "run";
  clone.tickInTurn = 0;

  const forecast: ForecastEntry[] = [];
  const seen = new Set<string>();

  for (let tick = 0; tick < clone.stage.ticksPerTurn; tick++) {
    if (clone.isRunComplete || clone.isRunFailed) break;

    const events = runTick(clone);
    clone.tickInTurn = tick + 1;

    for (const event of events) {
      const entry = forecastEntryFrom(event);
      if (!entry) continue;

      const key = `${entry.kind}:${entry.targetId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      forecast.push({ tickInTurn: tick, kind: entry.kind, targetId: entry.targetId });
    }
  }

  return forecast;
}
